<script setup lang="ts">
import { computed } from "vue";
import type { LocalModelType } from "@/features/BuildingProject";
import { Checkbox } from "@/features/Inputs";

/**
 * A project's models as a checklist: one checkbox row per model, the count and Select all / Clear on
 * the label line. The row is the one the dashboard's Models filter uses (Dashboard/components/Filter/
 * Models.vue), so a model reads the same wherever it is chosen; the frame is a form field's — a
 * `t-caption` label like the fields around it, no heading of its own, no divider — because this sits in
 * a form, not in a flyout with its own Apply. Presentational: the caller owns the list and flips the flags.
 */
const props = withDefaults(
  defineProps<{
    models: LocalModelType[];
    label?: string;
    caption?: string;
    /** What an empty list says. The caller knows why it is empty. */
    emptyText?: string;
  }>(),
  {
    label: "Models",
    caption: "",
    emptyText: "No suggested models for this country yet.",
  }
);

const emit = defineEmits<{
  toggleModel: [model: LocalModelType];
  setAll: [active: boolean];
}>();

const activeCount = computed(() => props.models.filter((model) => model.isActive).length);
const allActive = computed(() => props.models.length > 0 && activeCount.value === props.models.length);
</script>

<template>
  <div class="flex flex-col gap-1">
    <div class="gap-px-8 flex items-baseline justify-between">
      <!-- No label when the list sits inside a field's dropdown (ModelSelect): the field has already said it. -->
      <span
        v-if="label"
        class="t-caption text-type-muted">
        {{ label }}
      </span>
      <span
        v-if="models.length > 0"
        class="t-xs text-type-muted gap-px-8 ml-auto flex items-baseline">
        <span>{{ activeCount }} of {{ models.length }}</span>
        <button
          v-if="!allActive"
          type="button"
          class="text-app-blue-700 hover:text-app-blue-900 focus-ring rounded-4 underline-offset-2 hover:underline"
          @click="emit('setAll', true)">
          Select all
        </button>
        <button
          v-else
          type="button"
          class="hover:text-type-default focus-ring rounded-4 underline-offset-2 hover:underline"
          @click="emit('setAll', false)">
          Clear
        </button>
      </span>
    </div>
    <p
      v-if="caption"
      class="t-xs text-type-muted">
      {{ caption }}
    </p>

    <p
      v-if="models.length === 0"
      class="t-small text-type-muted pt-1">
      {{ emptyText }}
    </p>
    <!-- The label element wraps the input and carries the row's padding (p-px-8), so the row bleeds by
         that much on both sides: the checkbox lands on the form's left edge and the hover tint frames
         the whole row, as in the filter. -->
    <div
      v-else
      class="-mx-px-8 flex flex-col">
      <div
        v-for="model in models"
        :key="model.name"
        class="rounded-8 duration-fast ease-standard flex items-center transition-colors hover:bg-slate-100">
        <Checkbox
          :name="model.name"
          :checked="model.isActive"
          :label="model.name"
          @change="() => emit('toggleModel', model)" />
      </div>
    </div>
  </div>
</template>
